import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon, Maximize2, Minimize2 } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  useGenieStageContainer,
  isInsideGenieUi,
} from "@/lib/genie/stage-container";

// The stage decision (portal target + modal mode) is LATCHED while a dialog
// is open: switching either mid-flight remounts the dialog DOM, which shows
// as a blink and replays the open animation whenever Genie is toggled. A
// dialog keeps the mode it opened with and re-reads the live value once
// closed. `undefined` default = no Dialog wrapper above, fall back to live.
const LatchedStageContext = React.createContext<HTMLElement | null | undefined>(
  undefined
);

// Open state for the staged overlay: Radix renders no Overlay primitive for
// non-modal dialogs, so the stage backdrop is a plain div that needs
// data-state wired manually for the fade in/out animations to run.
const DialogOpenContext = React.createContext(false);

function useDialogStage(): HTMLElement | null {
  const latched = React.useContext(LatchedStageContext);
  const live = useGenieStageContainer();
  return latched === undefined ? live : latched;
}

function Dialog({
  modal,
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const liveStage = useGenieStageContainer();
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const isOpen = open ?? internalOpen;
  const [latchedStage, setLatchedStage] = React.useState<HTMLElement | null>(
    liveStage
  );
  React.useEffect(() => {
    if (!isOpen) setLatchedStage(liveStage);
  }, [isOpen, liveStage]);
  const stage = isOpen ? latchedStage : liveStage;
  // Non-modal while staged so Genie's scroll-lock doesn't freeze the panel.
  // Explicit `modal` wins.
  return (
    <DialogOpenContext.Provider value={isOpen}>
      <LatchedStageContext.Provider value={stage}>
        <DialogPrimitive.Root
          data-slot="dialog"
          modal={modal ?? stage === null}
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={(next) => {
            setInternalOpen(next);
            onOpenChange?.(next);
          }}
          {...props}
        />
      </LatchedStageContext.Provider>
    </DialogOpenContext.Provider>
  );
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  container,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  const stageContainer = useDialogStage();
  return (
    <DialogPrimitive.Portal
      data-slot="dialog-portal"
      container={container ?? stageContainer ?? undefined}
      {...props}
    />
  );
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return (
    <DialogPrimitive.Close
      data-slot="dialog-close"
      className="cursor-pointer"
      {...props}
    />
  );
}

function DialogOverlay({
  className,
  forceMount,
  asChild,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  const stageContainer = useDialogStage();
  const open = React.useContext(DialogOpenContext);
  const overlayClassName = cn(
    "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 inset-0 z-50 bg-black/50",
    stageContainer ? "absolute" : "fixed",
    className
  );

  if (stageContainer) {
    // Spread props so Presence's ref reaches the div, letting the close
    // animation finish before unmount, exactly like the modal overlay.
    return (
      <div
        data-slot="dialog-overlay"
        data-state={open ? "open" : "closed"}
        className={overlayClassName}
        {...props}
      />
    );
  }
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      forceMount={forceMount}
      asChild={asChild}
      className={overlayClassName}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      {...props}
    />
  );
}

type DialogContentSize =
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "6xl"
  | "7xl"
  | "full";

function DialogContent({
  className,
  children,
  showCloseButton = true,
  stopPropagation = false,
  size = "lg",
  canExpand = false,
  isExpanded = false,
  onToggleExpand,
  overlayClassName,
  headerButtonsClassName,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  size?: DialogContentSize;
  canExpand?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  stopPropagation?: boolean;
  overlayClassName?: string;
  headerButtonsClassName?: string;
}) {
  const effectiveSize = isExpanded ? "7xl" : size;
  const stageContainer = useDialogStage();

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay
        className={overlayClassName}
        onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
      />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        // Sizing must stay %-based (never vw/vh): when Genie is open the dialog
        // portals into the stage, whose overflow-hidden clips anything sized
        // against the viewport. % resolves against the containing block, which
        // is the viewport when fixed and the stage when absolute.
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] max-h-[90%] translate-x-[-50%] translate-y-[-50%] rounded-lg border shadow-lg duration-200",
          stageContainer ? "absolute" : "fixed",
          {
            "sm:max-w-[min(24rem,calc(100%-2rem))]": size === "sm",
            "sm:max-w-[min(28rem,calc(100%-2rem))]": size === "md",
            "sm:max-w-[min(32rem,calc(100%-2rem))]": size === "lg",
            "sm:max-w-[min(36rem,calc(100%-2rem))]": size === "xl",
            "sm:max-w-[min(42rem,calc(100%-2rem))]": size === "2xl",
            "sm:max-w-[min(48rem,calc(100%-2rem))]": size === "3xl",
            "sm:max-w-[min(56rem,calc(100%-2rem))]": size === "4xl",
            "sm:max-w-[min(64rem,calc(100%-2rem))]": size === "5xl",
            "sm:max-w-[min(72rem,calc(100%-2rem))]": size === "6xl",
            "sm:max-w-[min(80rem,calc(100%-2rem))]": size === "7xl",
            "sm:max-w-full": size === "full",
          },
          className
        )}
        onClick={(e) => {
          e.stopPropagation();
        }}
        // Only for pointer-downs on this dialog's own DOM. Portaled descendants
        // (a nested dialog's backdrop, a Select menu) bubble here through the
        // React tree only; stopping them keeps the native event from reaching
        // the document listener Radix uses to close the open Select/Popover.
        onPointerDown={(e) => {
          if (e.currentTarget.contains(e.target as Node)) e.stopPropagation();
        }}
        onInteractOutside={(event) => {
          onInteractOutside?.(event);
          if (event.defaultPrevented) return;
          if (isInsideGenieUi(event.target)) {
            event.preventDefault();
          }
        }}
        // Radix only suppresses focus-outside dismissal for MODAL dialogs;
        // non-modal ones (open while the Genie stage is active) close the
        // moment focus lands outside — e.g. on body when the Genie panel
        // unmounts. A dialog should never dismiss from focus movement.
        onFocusOutside={(event) => {
          event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          document.body.style.pointerEvents = "";
        }}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        {...props}
      >
        {children}
        <div
          className={cn(
            "absolute top-4 right-4 flex items-center gap-3",
            headerButtonsClassName
          )}
        >
          {canExpand && onToggleExpand && (
            <button
              onClick={onToggleExpand}
              title={isExpanded ? "Minimize" : "Maximize"}
              className="opacity-70 transition-opacity hover:cursor-pointer hover:opacity-100 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0"
            >
              {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              <span className="sr-only">
                {isExpanded ? "Minimize" : "Maximize"}
              </span>
            </button>
          )}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground rounded-xs opacity-70 transition-opacity hover:cursor-pointer hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex flex-col gap-1 text-center sm:text-left px-5 pt-4",
        className
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end px-5 py-3 border-t border-border bg-[#FCFCFC] rounded-b-lg",
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-medium", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-muted-foreground text-sm text-pretty max-w-[60ch]",
        className
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
